/**
 * «Velg et banner» (banner studio, owner 2026-10-31): one of the mosque's banners as a program's picture, instead of
 * downloading it from «Bannere» and uploading it under «Bilder».
 *
 * Lists what GET /admin/banners gives this person (every banner that is not taken down, drafts included — the writer
 * chose it, and the copy becomes the program's own picture), drawn as the screens draw them, newest first, with a
 * search box. Picking one hands the row back; the caller draws it (lib/banner-attach) and sends it with the form.
 * Shown only where the mosque has banners and the person may read them (the list route refuses anyone else).
 */
import React, { useMemo, useState } from 'react';
import { can, type Body } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { Btn, LoadError, Loading, Tag, type TagTone } from './ui';
import { FormDialog } from './dialog';
import { BannerThumb } from './BannerFace';
import { bannerTitle } from '../lib/banner-attach';

/** Whether this person may pick a banner at all: the mosque has them, and the list route answers this person. */
export const canPickBanner = (caps: string[] | null | undefined): boolean =>
  Boolean(caps && caps.includes('banners') && (can('screens.manage') || can('banners.approve')));

const STATE: Record<string, [string, TagTone]> = {
  draft: ['utkast', 'grey'], scheduled: ['publisert, vises senere', 'gold'], live: ['vises nå', 'green'], expired: ['utløpt', 'grey'], cancelled: ['stoppet', 'grey'],
};

export function BannerPicker({ open, onClose, onPick, busy = false }: { open: boolean; onClose: () => void; onPick: (b: Body) => void; busy?: boolean }) {
  const { caps } = useNav();
  const { data, error, reload } = useLoad<Body>(open && canPickBanner(caps) ? '/banners' : null, [open]);
  const [q, setQ] = useState('');
  const items: Body[] = useMemo(() => ((data && data.items) || []).filter((b: Body) => b.state !== 'archived')
    .filter((b: Body) => !q.trim() || bannerTitle(b).toLowerCase().includes(q.trim().toLowerCase())), [data, q]);
  return (
    <FormDialog open={open} wide title="Velg et banner" onClose={onClose}
      hint="Banneret tegnes i skjermstørrelse (1920×1080) og blir arrangementets bilde — i appen, på skjermene og på nettsiden. Endrer du banneret senere, kan bildet lages på nytt fra «Bilder».">
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <div className="field"><label htmlFor="bnr-pick-q">Søk</label>
            <input id="bnr-pick-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Overskrift …" autoComplete="off" /></div>
          {items.length ? (
            <div className="bnr-pick" role="list">
              {items.map((b) => {
                const [word, tone] = STATE[b.state] || [b.state, 'grey'];
                const title = bannerTitle(b);
                return (
                  <div key={b.id} role="listitem" className="bnr-pick-item">
                    <BannerThumb b={b} />
                    <div className="bnr-pick-meta">
                      <div className="nm">{title}</div>
                      <div className="sub">{b.template === 'image' ? 'Bilde' : b.templateName || b.template} · <Tag tone={tone}>{word}</Tag></div>
                    </div>
                    <Btn disabled={busy} aria-label={`Bruk «${title}»`} onClick={() => onPick(b)}>Bruk</Btn>
                  </div>
                );
              })}
            </div>
          ) : <p className="note">{q.trim() ? 'Ingen bannere passer søket.' : 'Ingen bannere ennå. Lag ett under «Bannere».'}</p>}
          <div className="row" style={{ marginTop: 12 }}><Btn ghost onClick={onClose}>Avbryt</Btn></div>
        </>
      )}
    </FormDialog>
  );
}
